// Tutorials — the table of contents: three tiers as a page, each row with its minutes and whether this device has finished
// it. Replaces "Show me around" (the menu row now reads "Show me around · Tutorials", because that phrase is what a lost
// user looks for). Opened from the account menu on the GM workspace and the player home; mounted once in app.tsx, like
// WhatsNew, and reached by `openTutorials()`.
import { useCallback, useEffect, useState } from "react";
import { AREA_LABEL, byTier, childrenOf, type Area, type Tutorial } from "../lib/tutorials";
import { isTourDone, savedStep } from "../lib/tours";
import { openTour } from "./tour";

export const TUTORIALS_EVENT = "compendium:tutorials";
export function openTutorials(): void { window.dispatchEvent(new CustomEvent(TUTORIALS_EVENT)); }

const AREAS: Area[] = ["prep", "table", "players", "data", "playing"];

function Row({ t, onStart }: { t: Tutorial; onStart: (id: string) => void }) {
  const done = isTourDone(t.id);
  const paused = !done && !!savedStep(t.id);
  return (
    <li className={`tut-row tier${t.tier}${done ? " done" : ""}`}>
      <span className="tut-mark" aria-hidden="true">{done ? "✓" : paused ? "…" : ""}</span>
      <span className="tut-text">
        <span className="tut-title">{t.title}</span>
        <span className="tut-blurb muted">{t.blurb}</span>
      </span>
      <span className="tut-min muted tiny">{t.minutes} min</span>
      <button className="tut-start" onClick={() => onStart(t.id)}>{done ? "Start over" : paused ? "Resume" : "Start"}</button>
    </li>
  );
}

export function TutorialsPage({ role }: { role: "gm" | "player" }) {
  const [open, setOpen] = useState(false);
  const show = useCallback(() => setOpen(true), []);
  useEffect(() => {
    window.addEventListener(TUTORIALS_EVENT, show);
    return () => window.removeEventListener(TUTORIALS_EVENT, show);
  }, [show]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  if (!open) return null;

  const start = (id: string) => { setOpen(false); openTour(id); };
  const first = byTier(1).filter((t) => t.role === role);
  const areas = AREAS.filter((a) => byTier(2).some((t) => t.area === a && t.role === role));
  return (
    <div className="modal-back topmost" onClick={() => setOpen(false)}>
      <div className="modal tutorials" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Tutorials">
        <div className="whatsnew-head">
          <h2 className="panel-title">Tutorials</h2>
          <button className="primary" onClick={() => setOpen(false)}>Close</button>
        </div>
        <p className="muted tiny">Short walkthroughs on the live app, one thing at a time. Each points at the real control; most take under two minutes. The <span className="tut-help inline" aria-hidden="true">?</span> on a tool opens that tool's.</p>

        <section className="tut-tier">
          <h3>First five minutes</h3>
          <ul className="tut-list">{first.map((t) => <Row key={t.id} t={t} onStart={start} />)}</ul>
        </section>

        {areas.map((a) => {
          const area = byTier(2).find((t) => t.area === a && t.role === role)!;
          const tools = childrenOf(area.id);
          return (
            <section key={a} className="tut-tier">
              <h3>{AREA_LABEL[a]}</h3>
              <ul className="tut-list">
                <Row t={area} onStart={start} />
                {tools.map((t) => <Row key={t.id} t={t} onStart={start} />)}
              </ul>
              {tools.length === 0 && <p className="muted tiny">Tool-by-tool walkthroughs for this area are on their way.</p>}
            </section>
          );
        })}
      </div>
    </div>
  );
}
